import { RoleNameEnum, type Role } from '@/gql/myomr'
import { useMeStore } from '@/stores'

export abstract class ApplicationPolicy {
  public static roles() {
    const meStore = useMeStore()
    return meStore.currentRoles as Role[]
  }

  public static currentPartnerCompanyId() {
    const meStore = useMeStore()
    return meStore.currentPartnerCompanyId
  }

  public static hasManageRole(roleName: string) {
    const hasRole = this.roles().find((role) => {
      return role.name === roleName && role.resourceType === 'PartnerCompany' && role.resourceId === this.currentPartnerCompanyId()
    })

    return !!hasRole
  }

  // helper methods
  public static hasContentPagesAccess() {
    return this.hasManageRole(RoleNameEnum.ManageContentPages)
  }

  public static hasUserManagementAccess() {
    return this.hasManageRole(RoleNameEnum.ManageUserManagement)
  }

  public static hasReviewsManagementAccess() {
    return this.hasManageRole(RoleNameEnum.ManageReviewsManagement)
  }

  public static hasOnlyReviewsRoles() {
    const allowedRoleNames: RoleNameEnum[] = [
      RoleNameEnum.ManageDashboard,
      RoleNameEnum.ManageUserManagement,
      RoleNameEnum.ManageReviewsManagement,
    ]

    return this.hasReviewsManagementAccess() && this.roles().every((role) => allowedRoleNames.includes(role.name))
  }

  public static hasSpeakersAccess() {
    return this.hasManageRole(RoleNameEnum.ManageSpeakers)
  }

  public static hasEventsOrQuotaAccess() {
    const quotaUserRole = this.roles().find((role) => {
      return role.name === RoleNameEnum.QuotaUser
    })

    return this.hasEventsAccess() || !!quotaUserRole
  }

  public static hasEventsAccess() {
    return this.hasManageRole(RoleNameEnum.ManageEvents)
  }

  public static hasTicketsAccess() {
    const ticketsRole = this.hasManageRole(RoleNameEnum.ManageTickets)
    const quotaUserRole = this.roles().find((role) => role.name === RoleNameEnum.QuotaUser)

    return ticketsRole || quotaUserRole
  }

  public static hasCompanyProfileAccess() {
    return this.hasManageRole(RoleNameEnum.ManageCompanyProfile)
  }

  public static hasBoothsAccess() {
    return this.hasManageRole(RoleNameEnum.ManageBooths)
  }

  public static hasMyLeadsAccess() {
    return this.hasManageRole(RoleNameEnum.ManageMyLeads)
  }

  public static hasRecruitingPlusAccess() {
    return this.hasManageRole(RoleNameEnum.ManageRecruitingPlus)
  }

  public static hasVisitorProfilesAccess() {
    return this.hasManageRole(RoleNameEnum.ManageVisitorProfiles)
  }

  public static hasLeadGenerationRole() {
    return this.hasManageRole(RoleNameEnum.ManageLeadGeneration)
  }

  public static hasMeetingsAccess() {
    return this.hasManageRole(RoleNameEnum.ManageMeetings)
  }

  public static hasFestivalShopAccess() {
    return this.hasManageRole(RoleNameEnum.ManageFestivalShop)
  }
}
